import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import UserAuthService from '../services/userAuthService';
import { User, UserContainer, LoginResponse } from '../models/User';

interface UserAuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  containers: UserContainer[];
  userAuthService: UserAuthService | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ mfaRequired?: boolean; challengeId?: string }>;
  verifyOtp: (challengeId: string, otp: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
  refreshContainers: () => Promise<void>;
  clearError: () => void;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

export const UserAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [containers, setContainers] = useState<UserContainer[]>([]);
  const [userAuthService] = useState<UserAuthService>(new UserAuthService());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        if (userAuthService.isAuthenticated()) {
          // Load user info from localStorage
          const userDataStr = localStorage.getItem('userData');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            setUser(userData);
            setIsAuthenticated(true);
            
            // Load containers
            try {
              const userContainers = await userAuthService.getUserContainers();
              setContainers(userContainers);
            } catch (err: any) {
              console.error('Failed to load containers:', err);
              // Don't fail auth if containers can't be loaded
            }
          } else {
            // Token exists but no user data - logout
            userAuthService.logout();
            setIsAuthenticated(false);
          }
        }
      } catch (err: any) {
        console.error('Auth check failed:', err);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [userAuthService]);

  // Finish a successful login (token already stored by the service).
  const completeLogin = async (response: LoginResponse): Promise<void> => {
    const userData: User = {
      userId: response.userId,
      email: response.email,
      displayName: response.displayName,
      status: 'active',
      canCreateContainers: response.canCreateContainers ?? false,
    };
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('userData', JSON.stringify(userData));
    try {
      const userContainers = await userAuthService.getUserContainers();
      setContainers(userContainers);
    } catch (err) {
      console.error('Failed to load containers after login:', err);
      setContainers([]);
    }
  };

  const login = async (email: string, password: string): Promise<{ mfaRequired?: boolean; challengeId?: string }> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await userAuthService.login(email, password);
      if ((response as any).mfaRequired) {
        return { mfaRequired: true, challengeId: (response as any).challengeId };
      }
      await completeLogin(response as LoginResponse);
      return {};
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.message || 'Login failed. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (challengeId: string, otp: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await userAuthService.verifyOtp(challengeId, otp);
      await completeLogin(response);
    } catch (err: any) {
      console.error('OTP verification failed:', err);
      setError(err.message || 'Verification failed. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, displayName: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await userAuthService.register(email, password, displayName);
      // Registration successful - don't auto-login, let user login manually
    } catch (err: any) {
      console.error('Registration failed:', err);
      setError(err.message || 'Registration failed. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    userAuthService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setContainers([]);
    setError(null);
    localStorage.removeItem('userData');
  };

  // Auto-logout after 15 minutes of inactivity. Activity is shared across tabs
  // via localStorage, so working (or reading) in one tab keeps every tab signed
  // in; and a logout in any tab propagates to the others.
  useEffect(() => {
    if (!isAuthenticated) return;
    const IDLE_MS = 15 * 60 * 1000;
    const ACTIVITY_KEY = 'lastActivity';

    let lastMark = 0;
    const markActivity = () => {
      const now = Date.now();
      if (now - lastMark > 5000) { // throttle writes
        lastMark = now;
        localStorage.setItem(ACTIVITY_KEY, now.toString());
      }
    };
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    const interval = setInterval(() => {
      const la = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
      if (la && Date.now() - la > IDLE_MS) {
        sessionStorage.setItem('idleLogout', '1');
        logout();
      }
    }, 20000);

    // If another tab logs out (token removed), sign this tab out too.
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'userAuthToken' && e.newValue === null && isAuthenticated) {
        logout();
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActivity));
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshContainers = async (): Promise<void> => {
    if (!isAuthenticated) {
      return;
    }
    
    try {
      const userContainers = await userAuthService.getUserContainers();
      setContainers(userContainers);
    } catch (err: any) {
      console.error('Failed to refresh containers:', err);
      setError(err.message || 'Failed to load containers');
      throw err;
    }
  };

  // Memoised so consumers' effects that depend on clearError don't re-run every
  // render (which previously cleared login errors the instant they appeared).
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return (
    <UserAuthContext.Provider 
      value={{ 
        isAuthenticated, 
        user, 
        containers,
        userAuthService, 
        isLoading, 
        error,
        login,
        verifyOtp,
        register,
        logout,
        refreshContainers,
        clearError
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = (): UserAuthContextType => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};

